/**
 * Provider-specific HTTP handlers for the email emulation plugin.
 *
 * @module
 */

import crypto from 'node:crypto';
import type { EmulationRequest, EmulationResponse } from '../../plugin.js';
import { record } from './capture.js';
import {
  decodeBase64,
  extractGenericAttachments,
  isRecord,
  parseAddress,
  parseAddressList,
  parseRawMime,
  safeParseJson,
} from './parsing.js';
import type { EmailCtx } from './types.js';

/** SendGrid v3 - POST /v3/mail/send (returns 202 with X-Message-Id). */
export function handleSendgrid(req: EmulationRequest, ctx: EmailCtx): EmulationResponse {
  const parsed = safeParseJson(req.body);
  if (!parsed.ok) return { status: 400, body: { errors: [{ message: 'Invalid JSON body' }] } };
  if (!isRecord(parsed.data))
    return { status: 400, body: { errors: [{ message: 'Invalid JSON body' }] } };
  const data = parsed.data;

  const personalizations: unknown[] = Array.isArray(data.personalizations)
    ? data.personalizations
    : [];
  const to: string[] = [];
  const cc: string[] = [];
  const bcc: string[] = [];
  let subject: string | null = typeof data.subject === 'string' ? data.subject : null;
  for (const p of personalizations) {
    const pers = isRecord(p) ? p : {};
    to.push(...parseAddressList(pers.to).emails);
    cc.push(...parseAddressList(pers.cc).emails);
    bcc.push(...parseAddressList(pers.bcc).emails);
    if (!subject && typeof pers.subject === 'string') subject = pers.subject;
  }

  const fromAddr = parseAddress(data.from);
  const content = Array.isArray(data.content) ? data.content.filter(isRecord) : [];
  const textValue = content.find((c) => c.type === 'text/plain')?.value;
  const htmlValue = content.find((c) => c.type === 'text/html')?.value;
  const text = typeof textValue === 'string' ? textValue : null;
  const html = typeof htmlValue === 'string' ? htmlValue : null;

  const captured = record(ctx, {
    provider: 'sendgrid',
    from: fromAddr?.email ?? '',
    ...(fromAddr?.name ? { fromName: fromAddr.name } : {}),
    to,
    cc,
    bcc,
    subject,
    text,
    html,
    headers: { 'x-provider': 'sendgrid' },
    attachments: extractGenericAttachments(data.attachments, 'filename', 'type'),
  });

  return { status: 202, headers: { 'x-message-id': captured.id }, body: '' };
}

/** Resend - POST /emails (returns 200 {id}). */
export function handleResend(req: EmulationRequest, ctx: EmailCtx): EmulationResponse {
  const parsed = safeParseJson(req.body);
  if (!parsed.ok)
    return { status: 400, body: { message: 'Invalid JSON body', name: 'validation_error' } };
  if (!isRecord(parsed.data))
    return { status: 400, body: { message: 'Invalid JSON body', name: 'validation_error' } };
  const data = parsed.data;

  const fromAddr = parseAddress(data.from);
  const captured = record(ctx, {
    provider: 'resend',
    from: fromAddr?.email ?? '',
    ...(fromAddr?.name ? { fromName: fromAddr.name } : {}),
    to: parseAddressList(data.to).emails,
    cc: parseAddressList(data.cc).emails,
    bcc: parseAddressList(data.bcc).emails,
    subject: typeof data.subject === 'string' ? data.subject : null,
    text: typeof data.text === 'string' ? data.text : null,
    html: typeof data.html === 'string' ? data.html : null,
    headers: { 'x-provider': 'resend' },
    attachments: extractGenericAttachments(data.attachments, 'filename', 'content_type'),
  });

  return { status: 200, body: { id: captured.id } };
}

/** Postmark - POST /email (returns 200 {MessageID, ErrorCode:0}). */
export function handlePostmarkSingle(req: EmulationRequest, ctx: EmailCtx): EmulationResponse {
  const parsed = safeParseJson(req.body);
  if (!parsed.ok) return { status: 422, body: { ErrorCode: 300, Message: 'Invalid JSON body' } };
  if (!isRecord(parsed.data))
    return { status: 422, body: { ErrorCode: 300, Message: 'Invalid JSON body' } };
  return { status: 200, body: postmarkRecord(ctx, parsed.data) };
}

/** Postmark - POST /email/batch (returns 200 [{MessageID}, ...]). */
export function handlePostmarkBatch(req: EmulationRequest, ctx: EmailCtx): EmulationResponse {
  const parsed = safeParseJson(req.body);
  if (!parsed.ok || !Array.isArray(parsed.data)) {
    return { status: 422, body: { ErrorCode: 300, Message: 'Invalid batch body' } };
  }
  return {
    status: 200,
    body: parsed.data.map((entry) => postmarkRecord(ctx, isRecord(entry) ? entry : {})),
  };
}

function postmarkRecord(ctx: EmailCtx, data: Record<string, unknown>) {
  const fromAddr = parseAddress(typeof data.From === 'string' ? data.From : null);
  const captured = record(ctx, {
    provider: 'postmark',
    from: fromAddr?.email ?? '',
    ...(fromAddr?.name ? { fromName: fromAddr.name } : {}),
    to: parseAddressList(data.To).emails,
    cc: parseAddressList(data.Cc).emails,
    bcc: parseAddressList(data.Bcc).emails,
    subject: typeof data.Subject === 'string' ? data.Subject : null,
    text: typeof data.TextBody === 'string' ? data.TextBody : null,
    html: typeof data.HtmlBody === 'string' ? data.HtmlBody : null,
    headers: { 'x-provider': 'postmark' },
    attachments: extractGenericAttachments(data.Attachments, 'Name', 'ContentType'),
  });
  return {
    To: parseAddressList(data.To).emails.join(', '),
    SubmittedAt: captured.receivedAt,
    MessageID: captured.id,
    ErrorCode: 0,
    Message: 'OK',
  };
}

/** Mailgun - POST /v3/:domain/messages (form-encoded, returns 200 {id, message}). */
export function handleMailgun(req: EmulationRequest, ctx: EmailCtx): EmulationResponse {
  const form = new URLSearchParams(req.body ?? '');
  const fromAddr = parseAddress(form.get('from'));
  const collect = (key: string) =>
    form.getAll(key).flatMap((value) => parseAddressList(value).emails);

  const captured = record(ctx, {
    provider: 'mailgun',
    from: fromAddr?.email ?? '',
    ...(fromAddr?.name ? { fromName: fromAddr.name } : {}),
    to: collect('to'),
    cc: collect('cc'),
    bcc: collect('bcc'),
    subject: form.get('subject'),
    text: form.get('text'),
    html: form.get('html'),
    headers: { 'x-provider': 'mailgun' },
    attachments: [],
  });

  return {
    status: 200,
    body: { id: `<${captured.id}@mailgun.emulated>`, message: 'Queued. Thank you.' },
  };
}

/** Amazon SES - POST / (SendEmail / SendRawEmail, query/form-encoded). */
export function handleSesV1(req: EmulationRequest, ctx: EmailCtx): EmulationResponse {
  const form = new URLSearchParams(req.body ?? '');
  const action = form.get('Action') ?? '';

  if (action === 'SendRawEmail') {
    const rawData = form.get('RawMessage.Data');
    const mime = rawData ? parseRawMime(decodeBase64(rawData)) : null;
    const captured = record(ctx, {
      provider: 'ses',
      from: mime?.from?.email ?? (form.get('Source') ?? '').toLowerCase(),
      ...(mime?.from?.name ? { fromName: mime.from.name } : {}),
      to: mime?.to ?? sesMemberList(form, 'Destinations.member'),
      cc: mime?.cc ?? [],
      bcc: mime?.bcc ?? [],
      subject: mime?.subject ?? null,
      text: mime?.text ?? null,
      html: mime?.html ?? null,
      headers: { 'x-provider': 'ses', 'x-ses-action': 'SendRawEmail' },
      attachments: [],
    });
    return sesXmlResponse('SendRawEmail', captured.id);
  }

  const captured = record(ctx, {
    provider: 'ses',
    from: (form.get('Source') ?? '').toLowerCase(),
    to: sesMemberList(form, 'Destination.ToAddresses.member'),
    cc: sesMemberList(form, 'Destination.CcAddresses.member'),
    bcc: sesMemberList(form, 'Destination.BccAddresses.member'),
    subject: form.get('Message.Subject.Data'),
    text: form.get('Message.Body.Text.Data'),
    html: form.get('Message.Body.Html.Data'),
    headers: { 'x-provider': 'ses', 'x-ses-action': action || 'SendEmail' },
    attachments: [],
  });
  return sesXmlResponse('SendEmail', captured.id);
}

/** Amazon SES v2 - POST /v2/email/outbound-emails (JSON). */
export function handleSesV2(req: EmulationRequest, ctx: EmailCtx): EmulationResponse {
  const parsed = safeParseJson(req.body);
  if (!parsed.ok) return { status: 400, body: { message: 'Invalid JSON body' } };
  if (!isRecord(parsed.data)) return { status: 400, body: { message: 'Invalid JSON body' } };
  const data = parsed.data;

  const dest = isRecord(data.Destination) ? data.Destination : {};
  const content = isRecord(data.Content) ? data.Content : {};
  const simple = isRecord(content.Simple) ? content.Simple : undefined;
  const raw = isRecord(content.Raw) ? content.Raw : undefined;

  let subject: string | null = null;
  let text: string | null = null;
  let html: string | null = null;
  let from = String(data.FromEmailAddress ?? '').toLowerCase();
  let to = parseAddressList(dest.ToAddresses).emails;
  let cc = parseAddressList(dest.CcAddresses).emails;
  let bcc = parseAddressList(dest.BccAddresses).emails;

  if (raw?.Data) {
    const mime = parseRawMime(decodeBase64(String(raw.Data)));
    if (mime.from) from = mime.from.email;
    if (mime.to.length) to = mime.to;
    if (mime.cc.length) cc = mime.cc;
    if (mime.bcc.length) bcc = mime.bcc;
    subject = mime.subject;
    text = mime.text;
    html = mime.html;
  } else if (simple) {
    const subjObj = isRecord(simple.Subject) ? simple.Subject : undefined;
    const bodyObj = isRecord(simple.Body) ? simple.Body : undefined;
    const textObj = bodyObj && isRecord(bodyObj.Text) ? bodyObj.Text : undefined;
    const htmlObj = bodyObj && isRecord(bodyObj.Html) ? bodyObj.Html : undefined;
    subject = subjObj?.Data ? String(subjObj.Data) : null;
    text = textObj?.Data ? String(textObj.Data) : null;
    html = htmlObj?.Data ? String(htmlObj.Data) : null;
  }

  const fromAddr = parseAddress(from);
  const captured = record(ctx, {
    provider: 'ses',
    from: fromAddr?.email ?? from,
    ...(fromAddr?.name ? { fromName: fromAddr.name } : {}),
    to,
    cc,
    bcc,
    subject,
    text,
    html,
    headers: { 'x-provider': 'ses', 'x-ses-action': 'SendEmailV2' },
    attachments: [],
  });

  return { status: 200, body: { MessageId: captured.id } };
}

function sesMemberList(form: URLSearchParams, prefix: string): string[] {
  const out: string[] = [];
  for (let i = 1; ; i++) {
    const value = form.get(`${prefix}.${i}`);
    if (value === null) break;
    const parsed = parseAddress(value);
    if (parsed) out.push(parsed.email);
  }
  return out;
}

function sesXmlResponse(
  action: 'SendEmail' | 'SendRawEmail',
  messageId: string
): EmulationResponse {
  const requestId = crypto.randomUUID();
  const body = `<${action}Response xmlns="http://ses.amazonaws.com/doc/2010-12-01/">
  <${action}Result>
    <MessageId>${messageId}</MessageId>
  </${action}Result>
  <ResponseMetadata>
    <RequestId>${requestId}</RequestId>
  </ResponseMetadata>
</${action}Response>`;
  return { status: 200, headers: { 'content-type': 'application/xml' }, body };
}
